import qs from "qs";
import { DEFAULT_ROOT_MEDIA_FOLDER_NAME, FileModel } from "dash-common";
import { useLocation, useNavigate } from "react-router-dom";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  FilterContext,
  PaginationContext,
  MarketingFiles as MarketingFilesComponent,
  FormContext,
  ViewerContext,
} from "dash-ui";

import {
  useGetAllFilesInPropertyQuery,
  useGetAllFoldersInPropertyQuery,
  useGetPortfolioMediaKitItemsQuery,
  useGetTopLevelFoldersInPropertyQuery,
} from "gql/generated.graphql";
import {
  downloadAllMediaInPortfolioMediaKit,
  downloadAllMediaInProperty,
  downloadMedia,
} from "common/helpers/downloadMedia";

import { RoutesPath } from "flow-components/routes/routes";

import { IMarketingFilesProps } from "./MarketingFiles.interfaces";
import { useGetQueryPagination } from "./MarketingFiles.hooks";
import {
  createGTMEvent,
  getGoogleTagManagerUserId,
} from "common/google-tag-manager/helpers";
import { GTMEventsEnum } from "common/google-tag-manager/constants";
import { ManageMediaKitFormFields } from "containers/portfolio/ManageMediaKitModal/ManageMediaKitModal.constants";

const MarketingFiles = ({
  propertyId,
  isMediaKit,
  isAddedToMediaKit,
  portfolioId,
  showMediaKit,
  isPortfolioView = false,
  compactMediaList = false,
}: IMarketingFilesProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    state: paginationState,
    getRefetchProps,
    setTotal,
    resetPagination,
  } = useContext(PaginationContext);
  const { formState, stateHandlers } = useContext(FormContext);
  const [folderId, setFolderId] = useState("");
  const { account } = useContext(ViewerContext);
  const [accessedFiles, setAccessedFiles] = useState<any[]>([]);
  const { state: filterState } = useContext(FilterContext);
  const { limit } = getRefetchProps(paginationState);
  const { addedMediaItems, removedMediaItems, paginationProps, updatedFiles } =
    useGetQueryPagination(isAddedToMediaKit, isMediaKit);

  const { data: foldersData, loading: foldersLoading } =
    useGetTopLevelFoldersInPropertyQuery({
      variables: { propertyId },
      skip: !propertyId,
    });

  const { folderId: parentFolderId } = useMemo(
    () => qs.parse(location.search.slice(1)),
    [location]
  );

  let tabsFolders = useMemo(
    () => foldersData?.getTopLevelFoldersInProperty || [],
    [foldersData]
  );

  const mediaFolderId = useMemo(
    () =>
      parentFolderId ||
      folderId ||
      (tabsFolders.length > 0 ? tabsFolders[0]._id : ""),
    [parentFolderId, tabsFolders, folderId]
  );

  const { data: innerFoldersData, loading: innerFoldersLoading } =
    useGetAllFoldersInPropertyQuery({
      variables: {
        propertyId,
        mediaFolderId,
      },
      skip:
        !mediaFolderId ||
        !propertyId ||
        !!tabsFolders.some(
          (item) =>
            item._id === mediaFolderId &&
            item.folderName === DEFAULT_ROOT_MEDIA_FOLDER_NAME
        ),
    });

  const innerFolders = useMemo(
    () => innerFoldersData?.getAllFoldersInProperty || [],
    [innerFoldersData]
  );

  // Track current folder tab and its children IDs
  useEffect(() => {
    stateHandlers[ManageMediaKitFormFields.CurrentFoldersIds]?.(
      filterState.foldersIds?.length > 0
        ? filterState.foldersIds
        : [
            ...(mediaFolderId ? [mediaFolderId] : []),
            ...innerFolders.map((folder) => folder._id),
          ]
    );
  }, [mediaFolderId, filterState, innerFolders]);

  const allFilesListVariables = useMemo(
    () => ({
      pagination: isMediaKit ? paginationProps : { limit },
      filter: {
        portfolioId,
        excludedMediaIds: removedMediaItems.map((item: FileModel) => item._id),
        mediaFolderIds:
          filterState.foldersIds?.length > 0
            ? filterState.foldersIds
            : [
                ...(mediaFolderId ? [mediaFolderId] : []),
                ...innerFolders.map((folder) => folder._id),
              ],
      },
    }),
    [
      portfolioId,
      paginationProps,
      removedMediaItems,
      filterState.foldersIds,
      mediaFolderId,
      innerFolders,
      isMediaKit,
      limit,
    ]
  );

  const {
    data: allFilesInProperty,
    loading: filesInPropertyLoading,
    fetchMore: fetchMoreFilesInProperty,
  } = useGetAllFilesInPropertyQuery({
    variables: {
      ...allFilesListVariables,
      filter: {
        ...allFilesListVariables.filter,
        propertyId,
        excludedMediaIds: addedMediaItems.map((item: FileModel) => item._id),
      },
    },
    fetchPolicy: "network-only",
    skip: !propertyId || isAddedToMediaKit,
  });
  const {
    data: allFilesInPortfolio,
    loading: filesInPortfolioLoading,
    fetchMore: fetchMoreFilesInPortfolio,
  } = useGetPortfolioMediaKitItemsQuery({
    variables: allFilesListVariables,
    fetchPolicy: "network-only",
    skip: !portfolioId || !isAddedToMediaKit,
  });

  const { allFilesCollection, allFilesCollectionTotal } = useMemo(
    () =>
      isAddedToMediaKit
        ? {
            allFilesCollection: (
              allFilesInPortfolio?.getPortfolioMediaKitItems.collection || []
            ).map(({ media, isAddedToMediaKit }) => ({
              ...media,
              isAddedToMediaKit,
            })),
            allFilesCollectionTotal:
              allFilesInPortfolio?.getPortfolioMediaKitItems.totalCount || 0,
          }
        : {
            allFilesCollection:
              allFilesInProperty?.getAllFilesInProperty.filesCollection || [],
            allFilesCollectionTotal:
              allFilesInProperty?.getAllFilesInProperty.filesTotalCount || 0,
          },
    [allFilesInProperty, isAddedToMediaKit, allFilesInPortfolio]
  );

  const createGTMForMultipleDownload = useCallback(
    (toDownload: string[]) => {
      if (!account) return;
      toDownload.forEach((fileToDownload) => {
        const file = accessedFiles?.find((af) => af._id === fileToDownload);
        const folder = tabsFolders.find(({ _id }) => _id === file?.folderId);
        createGTMEvent({
          dataLayer: {
            event: GTMEventsEnum.FILE_DOWNLOAD,
            user_id: getGoogleTagManagerUserId(account.email),
            file_extension: folder?.folderName,
            file_name: file?.name,
          },
        });
      });
    },
    [account, accessedFiles, tabsFolders]
  );

  const onDownloadSelectedClick = (selected: Record<string, boolean>) => {
    const toDownload = Object.keys(selected).filter((key) => selected[key]);
    if (toDownload.length) {
      createGTMForMultipleDownload(toDownload);
      return downloadMedia(toDownload);
    }

    if (isAddedToMediaKit && portfolioId) {
      createGTMForMultipleDownload(files.map(({ _id }) => _id));
      return downloadAllMediaInPortfolioMediaKit(portfolioId);
    }
    createGTMForMultipleDownload(files.map(({ _id }) => _id));
    return downloadAllMediaInProperty(propertyId);
  };

  useEffect(() => {
    setTotal(
      allFilesCollectionTotal +
        (!isAddedToMediaKit ? removedMediaItems.length : addedMediaItems.length)
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allFilesCollectionTotal,
    isAddedToMediaKit,
    removedMediaItems,
    addedMediaItems,
  ]);

  useEffect(() => {
    setFolderId(tabsFolders[0]?._id || "");
  }, [tabsFolders]);

  const fetchedFiles = useMemo(
    () =>
      allFilesCollection.map((item: any) => {
        const file = item.files[0];

        return {
          _id: item._id,
          name: file.name,
          size: item.sizeInBytes,
          publicUrl: file.publicUrl,
          thumbnailFileUrl: file.thumbnailFileUrl,
          createdAt: file.createdAt,
          type: file.type,
          mediaFolderId: item.mediaFolderId,
          isAddedToMediaKit: item.isAddedToMediaKit,
          s3Key: file.s3Key,
        };
      }),
    [allFilesCollection]
  );

  const files: FileModel[] = useMemo(() => {
    const updatedFilesIds = updatedFiles.map((item: FileModel) => item._id);

    return [
      ...updatedFiles,
      ...fetchedFiles.filter((item) => !updatedFilesIds.includes(item._id)),
    ];
  }, [fetchedFiles, updatedFiles]);

  // GTM related
  useEffect(() => {
    const accessedIds = accessedFiles.map(({ _id }) => _id);
    const newFiles = fetchedFiles.filter(
      ({ _id }) => !accessedIds.includes({ _id })
    );
    const newFilesWithFolder = newFiles.map((newFile) => {
      return { ...newFile, folderId: parentFolderId || tabsFolders[0]?._id };
    });
    setAccessedFiles((prevAccessedFiles) => [
      ...prevAccessedFiles,
      ...newFilesWithFolder,
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedFiles]);

  const updateFormStateItems = useCallback(
    (formField: ManageMediaKitFormFields) => (file: FileModel) => {
      const formFieldToRemove =
        formField === ManageMediaKitFormFields.RemovedMedia
          ? ManageMediaKitFormFields.AddedMedia
          : ManageMediaKitFormFields.RemovedMedia;

      stateHandlers[formFieldToRemove](
        formState[formFieldToRemove]?.map((item: any) => {
          if (item.portfolioId === formState.currentPortfolioId) {
            return {
              ...item,
              mediaIds: item.mediaIds.filter(
                (mediaFile: FileModel) => mediaFile._id !== file._id
              ),
            };
          }
          return item;
        })
      );
      stateHandlers[formField](
        formState[formField]
          ? formState[formField].map((item: any) => {
              if (item.portfolioId === formState.currentPortfolioId) {
                const isExist = item.mediaIds.some(
                  (mediaFile: FileModel) => mediaFile._id === file._id
                );
                return {
                  ...item,
                  mediaIds: isExist ? item.mediaIds : [...item.mediaIds, file],
                };
              }
              return item;
            })
          : [{ portfolioId: formState.currentPortfolioId, mediaIds: [file] }]
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formState, stateHandlers, paginationState.total]
  );

  const setActiveTab = useCallback(
    (tab: string) => {
      if (isMediaKit || isAddedToMediaKit) {
        return setFolderId(tab);
      }
      navigate(
        RoutesPath.PropertyPreview.replace(":id", propertyId) +
          `?folderId=${tab}`
      );
    },
    [isMediaKit, propertyId, navigate, isAddedToMediaKit]
  );

  // Reset pagination everytime user selects different property.
  useEffect(() => {
    resetPagination();
  }, [portfolioId]);

  return (!portfolioId || isAddedToMediaKit || showMediaKit) ? (
    <MarketingFilesComponent
      isPortfolioView={isPortfolioView}
      tabs={tabsFolders.map((item) => ({
        label: item.folderName,
        key: item._id,
      }))}
      files={files as any}
      folders={innerFolders as any}
      activeTab={(parentFolderId as string) || folderId}
      onDownloadClick={onDownloadSelectedClick}
      isMediaKit={isMediaKit}
      compactMediaList={compactMediaList}
      loading={
        foldersLoading ||
        filesInPropertyLoading ||
        innerFoldersLoading ||
        filesInPortfolioLoading
      }
      fetchMoreProps={
        isAddedToMediaKit
          ? {
              fetchMore: fetchMoreFilesInPortfolio,
              variables: allFilesListVariables,
              queryName: "getPortfolioMediaKitItems",
              collectionName: "collection",
            }
          : {
              fetchMore: fetchMoreFilesInProperty,
              variables: {
                ...allFilesListVariables,
                filter: {
                  ...allFilesListVariables.filter,
                  propertyId,
                  excludedMediaIds: addedMediaItems.map(
                    (item: FileModel) => item._id
                  ),
                },
              },
              queryName: "getAllFilesInProperty",
              collectionName: "filesCollection",
            }
      }
      addToMediaKit={updateFormStateItems(ManageMediaKitFormFields.AddedMedia)}
      removeFromMediaKit={updateFormStateItems(
        ManageMediaKitFormFields.RemovedMedia
      )}
      setActiveTab={setActiveTab}
    /> 
  ) : null;
};

export default MarketingFiles;
